import versionJson from './version.json'
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
  RouterProvider
} from 'react-router'
import { useMemo } from 'react'
import { BASE_URL, PROVETA_URL } from './constants'
import {
  AuthProvider,
  type AppVersionMeta,
  ConfirmUpdateModal,
  ErrorScreen,
  LoadingScreen,
  LoginScreen,
  NewsListScreen,
  PrivateOutlet,
  RouteMetadata,
  RoleGuard,
  AccessDeniedScreen,
  SampleChangelogScreen,
  SchemaScreen,
  SelectDivisionScreen,
  SelectThemeScreen,
  SettingsScreen,
  ThemeProvider,
  TopBar,
  useAutoUpdater,
  SampleScreen
} from 'prove-common'
import PlansScreen from './screens/PlansScreen'
import PlanScreen from './screens/PlanScreen'
import TaskScreen from './screens/TaskScreen'
import TasksScreen from './screens/TasksScreen'
import ContractsScreen from './screens/ContractsScreen'
import ContractScreen from './screens/ContractScreen'
import SamplesScreen from './screens/SamplesScreen'
import { StatisticsScreen } from './screens/StatisticsScreen'
import WorkspacesScreen from './screens/WorkspacesScreen'
import { SideBarLayout } from './components/side-bar-layout/SideBarLayout'
import { SelectDivisionScreenTopBar } from './components/screen-top-bars/SelectDivisionScreenTopBar'
import { SelectThemeScreenTopBar } from './components/screen-top-bars/SelectThemeScreenTopBar'
import { NewsListScreenTopBar } from './components/screen-top-bars/NewsListScreenTopBar'
import { ChangelogScreenTopBar } from './components/screen-top-bars/ChangelogScreenTopBar'
import { ImportantMessagesScreen } from './screens/ImportantMessagesScreen'
import { SchemaScreenTopBar } from './components/screen-top-bars/SchemaScreenTopBar'
import { AppLogo } from './components/app-logo/AppLogo'
import { TaskMoveScreen } from './screens/TaskMoveScreen'
import { RedirectRequest } from '@azure/msal-browser'
import { betaFeatures } from './betaFeatures'

const getRoutes = (versionMeta: AppVersionMeta): RouteObject[] => [
  {
    element: <RouteMetadata appName='Prøveplan' />,
    errorElement: (
      <RouteMetadata appName='Prøveplan'>
        <ErrorScreen logo={<AppLogo />} useMobileBar={false} />
      </RouteMetadata>
    ),
    children: [
      {
        path: '/login',
        handle: { title: 'Logg inn' },
        element: (
          <LoginScreen
            logo={<AppLogo />}
            loginButtonText={'Logg inn'}
            homeUrl='/plans'
          />
        )
      },
      {
        path: '/unauthorized',
        handle: { title: 'Ingen tilgang' },
        element: <AccessDeniedScreen />
      },
      {
        element: (
          <PrivateOutlet
            homeUrl='/plans'
            loadingElement={
              <LoadingScreen
                logo={<AppLogo />}
                loadingMessage='Laster inn...'
                useMobileBar={false}
              />
            }
            errorElement={
              <ErrorScreen logo={<AppLogo />} useMobileBar={false} />
            }
          />
        ),
        path: '/',
        children: [
          {
            element: <RoleGuard requiredRoles={['employee']} />,
            children: [
              {
                path: '/settings',
                handle: { title: 'Innstillinger' },
                element: (
                  <SettingsScreen
                    betaFeatures={betaFeatures}
                    versionMeta={versionMeta}
                    sidebar={<SideBarLayout />}
                    useMobileBar={false}
                    topBar={
                      <TopBar
                        title='Innstillinger'
                        logo={<AppLogo />}
                        showNotificationBell={true}
                      />
                    }
                  />
                )
              },
              {
                path: '/settings/select-division',
                handle: { title: 'Velg tilhørighet' },
                element: (
                  <SelectDivisionScreen
                    sidebar={<SideBarLayout />}
                    topBar={<SelectDivisionScreenTopBar />}
                    useMobileBar={false}
                  />
                )
              },
              {
                path: '/settings/select-theme',
                handle: { title: 'Velg tema' },
                element: (
                  <SelectThemeScreen
                    sidebar={<SideBarLayout />}
                    topBar={<SelectThemeScreenTopBar />}
                    useMobileBar={false}
                  />
                )
              },
              {
                path: '/newslist',
                handle: { title: 'Nyheter' },
                element: (
                  <NewsListScreen
                    sidebar={<SideBarLayout />}
                    topBar={<NewsListScreenTopBar />}
                    useMobileBar={false}
                  />
                )
              },
              {
                path: '/workspaces',
                handle: { title: 'Arbeidsområder' },
                element: <WorkspacesScreen />
              },
              {
                path: '/plans',
                handle: { title: 'Planer' },
                element: <PlansScreen />
              },
              {
                path: '/tasks',
                handle: { title: 'Oppgaver' },
                element: <TasksScreen />
              },
              {
                path: '/plans/:planId',
                handle: { title: 'Plandetaljer' },
                element: <PlanScreen />
              },
              {
                path: '/plans/:planId/tasks/:taskId',
                handle: { title: 'Oppgavedetaljer' },
                element: <TaskScreen />
              },
              {
                path: '/important-messages',
                handle: { title: 'Viktige meldinger' },
                element: <ImportantMessagesScreen />
              },
              {
                path: '/contracts/',
                handle: { title: 'Kontrakter' },
                element: <ContractsScreen />
              },
              {
                path: '/contracts/:contractId',
                handle: { title: 'Kontraktdetaljer' },
                element: <ContractScreen />
              },
              {
                path: '/samples',
                handle: { title: 'Prøver' },
                element: <SamplesScreen />
              },
              {
                path: '/samples/:sampleId',
                handle: { title: 'Prøvedetaljer' },
                element: (
                  <SampleScreen
                    logo={<AppLogo />}
                    sideBar={<SideBarLayout />}
                    taskLinkTo={PROVETA_URL}
                    sampleActionsBaseUrl={PROVETA_URL}
                    useMobileBar={false}
                  />
                )
              },
              {
                path: '/samples/:sampleId/changelog',
                handle: { title: 'Endringslogg' },
                element: (
                  <SampleChangelogScreen
                    sidebar={<SideBarLayout />}
                    topBar={<ChangelogScreenTopBar />}
                    useMobileBar={false}
                  />
                )
              },
              {
                path: '/statistics',
                handle: { title: 'Statistikk' },
                element: <StatisticsScreen />
              },
              {
                path: '/overview',
                handle: { title: 'Statistikk' },
                element: <Navigate to='/statistics' replace />
              },
              {
                path: '/contracts/:contractId/schemas/:payloadSchemaId',
                handle: { title: 'Registreringsskjema' },
                element: (
                  <SchemaScreen
                    sidebar={<SideBarLayout />}
                    topBar={<SchemaScreenTopBar />}
                    useMobileBar={false}
                  />
                )
              },
              {
                path: '/plans/:planId/tasks/:taskId/move',
                handle: { title: 'Flytt oppgave' },
                element: <TaskMoveScreen />
              }
            ]
          }
        ]
      }
    ]
  }
]

export default function App({
  loginRequest
}: Readonly<{
  loginRequest: RedirectRequest
}>) {
  const versionMeta: AppVersionMeta = versionJson

  // versionMeta comes from bundled `src/version.json`; avoid using the
  // runtime `/version.json` here because it can reflect a newer deploy.

  const { updateAvailable } = useAutoUpdater(versionJson)
  const browserRouter = useMemo(
    () => createBrowserRouter(getRoutes(versionMeta)),
    [versionMeta]
  )
  return (
    <AuthProvider loginRequest={loginRequest} baseUrl={BASE_URL}>
      <ThemeProvider>
        <RouterProvider router={browserRouter} />
        <ConfirmUpdateModal isOpen={updateAvailable} />
      </ThemeProvider>
    </AuthProvider>
  )
}
